import { BlockchainFiltersMoleculeReatom } from "@/app/blockchains/filters/blockchain-filters-molecule.reatom"
import { WalletAddressLink } from "@/app/components/wallet-address"
import { getItemHref } from "@/app/navigation/item"
import {
  NftMiniature,
  NftMiniatureSkeleton,
} from "@/app/nft/miniautre/nft-miniature"
import { NftsSelectionMolecule } from "@/app/nft/nfts-selection/nfts-selection-molecule"
import Rarity from "@/app/nft/rarity/rarity"
import { PriceWrapper } from "@/app/price"
import { Blockchain } from "@anaconda/core/blockchain/types"
import type { NftSearchItem } from "@anaconda/core/nft/search/types"
import type { NftId } from "@anaconda/core/nft/types"
import { Trans } from "@lingui/react/macro"
import { Skeleton, cn } from "@rarible_int/anaconda_ui_components"
import { useMolecule } from "bunshi/react"
import Link from "next/link"
import { useMemo } from "react"
import { Action } from "./action"
import { Cell, HeaderCell } from "./cells"
import { NftSelection } from "./nft-selection"

interface Column {
  Header: React.ComponentType
  Content: React.ComponentType<NftSearchItem>
  Skeleton: React.ComponentType
}

const ITEM_CELL_CLASSNAME =
  "sticky left-0 [&:not(:first-child)]:left-[32px] z-20"
const SELECTION_CELL_CLASSNAME = "sticky left-0 z-20"

/**
 * Left out on Solana pages: a Solana last sale counts sales through Rarible
 * only, which hardly any NFT has had yet, so the column read "—" throughout.
 */
const LAST_SALE_COLUMN: Column = {
  Header: () => (
    <HeaderCell className="w-[200px]">
      <Trans>Last sale</Trans>
    </HeaderCell>
  ),
  Content: (nft) => (
    <Cell>
      <NftLink className="justify-end" nftId={nft.id}>
        {nft.lastSellPrice ? (
          <PriceWrapper {...nft.lastSellPrice} />
        ) : (
          <UndefinedLabel />
        )}
      </NftLink>
    </Cell>
  ),
  Skeleton: () => (
    <Cell>
      <AlignRight>
        <Skeleton className="h-[22px] w-[100px]" />
      </AlignRight>
    </Cell>
  ),
}

export const BASE_COLUMNS: Column[] = [
  {
    Header: () => (
      <HeaderCell
        className={cn("w-[180px] text-left md:w-[250px]", ITEM_CELL_CLASSNAME)}
      >
        <Trans>Item</Trans>
      </HeaderCell>
    ),
    Content: (nft) => (
      <Cell className={ITEM_CELL_CLASSNAME}>
        <NftMiniature className="h-full p-2" nft={nft} />
      </Cell>
    ),
    Skeleton: () => (
      <Cell className={cn("p-2", ITEM_CELL_CLASSNAME)}>
        <NftMiniatureSkeleton />
      </Cell>
    ),
  },
  {
    Header: () => (
      <HeaderCell className="w-[160px] md:w-[200px]">
        <Trans>Rarity</Trans>
      </HeaderCell>
    ),
    Content: (nft) => (
      <Cell>
        <AlignRight>
          {nft.rarity ? <Rarity rarity={nft.rarity} /> : <UndefinedLabel />}
        </AlignRight>
      </Cell>
    ),
    Skeleton: () => (
      <Cell>
        <AlignRight>
          <Skeleton className="h-[22px] w-[100px]" />
        </AlignRight>
      </Cell>
    ),
  },
  {
    Header: () => (
      <HeaderCell className="w-[160px] md:w-[200px]">
        <Trans>List price</Trans>
      </HeaderCell>
    ),
    Content: (nft) => (
      <Cell>
        <AlignRight>
          {nft.price ? (
            <PriceWrapper
              amount={nft.price.amount}
              currency={nft.price.currency}
            />
          ) : (
            <UndefinedLabel />
          )}
        </AlignRight>
      </Cell>
    ),
    Skeleton: () => (
      <Cell>
        <AlignRight>
          <Skeleton className="h-[22px] w-[100px]" />
        </AlignRight>
      </Cell>
    ),
  },
  LAST_SALE_COLUMN,
  {
    Header: () => (
      <HeaderCell className="w-[200px]">
        <Trans>Owner</Trans>
      </HeaderCell>
    ),
    Content: (nft) => (
      <Cell>
        <AlignRight>
          {nft.owner ? (
            <WalletAddressLink address={nft.owner} prefetch={false} />
          ) : (
            <UndefinedLabel />
          )}
        </AlignRight>
      </Cell>
    ),
    Skeleton: () => (
      <Cell>
        <AlignRight>
          <Skeleton className="h-6 w-[108px]" />
        </AlignRight>
      </Cell>
    ),
  },
  {
    Header: () => (
      <HeaderCell className="w-[160px] md:w-[200px]">
        <Trans>Action</Trans>
      </HeaderCell>
    ),
    Content: (nft) => (
      <Cell>
        <AlignRight>
          <Action nft={nft} />
        </AlignRight>
      </Cell>
    ),
    Skeleton: () => (
      <Cell>
        <AlignRight>
          <Skeleton className="h-[22px] w-[100px]" />
        </AlignRight>
      </Cell>
    ),
  },
]

export const COLUMNS_WITH_SELECTION: Column[] = [
  {
    Header: () => (
      <HeaderCell className={cn("w-[32px]", SELECTION_CELL_CLASSNAME)}>
        {null}
      </HeaderCell>
    ),
    Content: (nft) => (
      <Cell className={SELECTION_CELL_CLASSNAME}>
        <div className="flex justify-center">
          <NftSelection nft={nft} />
        </div>
      </Cell>
    ),
    Skeleton: () => (
      <Cell className={SELECTION_CELL_CLASSNAME}>
        <Skeleton className="h-6 w-6" />
      </Cell>
    ),
  },
  ...BASE_COLUMNS,
]

export function useColumns() {
  const { selectionAvailable } = useMolecule(NftsSelectionMolecule)
  const { selectedBlockchain } = useMolecule(BlockchainFiltersMoleculeReatom)
  const blockchain = selectedBlockchain()

  return useMemo(() => {
    const columns = selectionAvailable ? COLUMNS_WITH_SELECTION : BASE_COLUMNS
    return blockchain === Blockchain.SOLANA
      ? columns.filter((column) => column !== LAST_SALE_COLUMN)
      : columns
  }, [selectionAvailable, blockchain])
}

function UndefinedLabel() {
  return <span>—</span>
}

function AlignRight({
  children,
  className,
}: { children: React.ReactNode; className?: string }) {
  return <div className={cn("flex justify-end p-2", className)}>{children}</div>
}

function NftLink({
  children,
  nftId,
  className,
}: { children: React.ReactNode; nftId: NftId; className?: string }) {
  return (
    <Link
      className={cn("flex h-full w-full p-2", className)}
      href={getItemHref(nftId)}
      prefetch={false}
    >
      {children}
    </Link>
  )
}
